import { lazy, Suspense, useEffect } from "react";

import { useUFOComponentExperience } from "@atlassian/ufo";

import SignupModule from "@/components/signup/bxp-ui-signup/src/signup-module";
import { FeatureFlags } from "@/components/signup/bxpkit-signup/src/model";
import { LayoutType } from "@/components/signup/bxpkit-signup/src/sign-up-layout-container";
import { Bundle, Edition } from "@/components/signup/utils";
import { ufoEventPaidSignupLandingPageLoad } from "@/components/signup/utils";
import TryIntent from "@/components/try-intent/src";
import {
    useDevAiIntent,
    useFreeIntent,
    useJiraIntent,
    useJiraServiceManagementIntent,
    useNewUser,
    useProductIntent,
    useUserContext
} from "@/components/user-context";
import {
    AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY,
    sendConversionEventAlgolia
} from "@/patterns/global-nav/src/auto-complete-helpers";
import {
    defaultFeatureFlags,
    defaultLabels,
    defaultProductData,
    getQueryParams,
    devopsImageUrls,
    firstImpressionsImageUrls,
    useLabelsLocalized
} from "@/templates/try/cloud/signup/signup-page/src/utils";
import {
    checkFeatureFlagGate,
    featureFlags as flags
} from "@/utils/feature-flags";
import { getLocalStorage } from "@/utils/localstorage";

import { ClientOnly, useLocation } from "../../../../../../utils/";

import { ErrorLocale } from "./components";
import { LoggedIn } from "./logged-in/logged-in";
import { LoggedOut } from "./logged-out";
import { handleSignupRouteRedirects } from "./utils/signup-redirection-resolver";

const SignUpLayoutContainer = lazy(
    () =>
        // @ts-ignore
        import(
            "../../../../../../components/signup/bxpkit-signup/src/sign-up-layout-container.tsx"
        )
);

type SignupPageTemplateProps = {
    layout?: LayoutType;
    featureFlags?: FeatureFlags;
};
export type SignupPageTemplateBaseProps = {
    product?: string;
    bundle?: string;
    migrationSourceUuid?: string;
    edition?: string;
    layout?: LayoutType;
    featureFlags?: FeatureFlags;
    isN2NParam?: boolean;
    useBlackSubmitButtonText?: boolean;
    developer?: string;
};

export const SignupPageTemplate = ({
    featureFlags
}: SignupPageTemplateProps) => {
    const location = useLocation();
    const { bundle: useProductIntentBundle } = useProductIntent(location.href);

    const {
        product,
        bundle,
        edition,
        isN2NParam,
        migrationSourceUuid,
        layout,
        developer
    } = getQueryParams(location.search);

    if (handleSignupRouteRedirects({ product: product as string, bundle })) {
        return <></>;
    }

    return (
        <SignupPageTemplateBase
            product={product ?? undefined}
            bundle={useProductIntentBundle}
            edition={edition}
            layout={layout}
            featureFlags={{
                ...defaultFeatureFlags,
                ...featureFlags
            }}
            migrationSourceUuid={migrationSourceUuid}
            isN2NParam={isN2NParam}
            developer={developer}
        />
    );
};

export const SignupPageTemplateBase = ({
    product,
    bundle,
    edition,
    layout,
    featureFlags,
    isN2NParam,
    migrationSourceUuid,
    developer
}: SignupPageTemplateBaseProps) => {
    useUFOComponentExperience(ufoEventPaidSignupLandingPageLoad);

    return (
        <Suspense>
            <div>
                <SignUpComponent
                    productParam={product}
                    bundleOverride={bundle}
                    editionOverride={edition}
                    layout={layout}
                    featureFlags={{
                        ...defaultFeatureFlags,
                        ...featureFlags
                    }}
                    isN2NParam={isN2NParam}
                    migrationSourceUuid={migrationSourceUuid}
                    developer={developer}
                />
            </div>
        </Suspense>
    );
};

// eslint-disable-next-line complexity
const SignUpComponent = ({
    productParam,
    bundleOverride,
    editionOverride,
    layout = "first-impressions",
    featureFlags,
    migrationSourceUuid,
    developer
}: {
    productParam?: string;
    bundleOverride?: string;
    editionOverride?: string;
    migrationSourceUuid?: string;
    layout?: LayoutType;
    isN2NParam?: boolean;
    developer?: string;
    featureFlags: FeatureFlags;
}) => {
    const { user: userProfile } = useUserContext();
    const isNewUser = useNewUser();
    const isFreeEdition = useFreeIntent();
    const isJSW = useJiraIntent();
    const isJSM = useJiraServiceManagementIntent();
    const isDevAI = useDevAiIntent();

    const countryCode = window.countryCode;
    const blockedLocales = ["RU", "BY"];
    const labels = useLabelsLocalized(defaultLabels);
    const siteActivationGate = checkFeatureFlagGate(flags.siteActivationGate);
    const jsmPaidEditionsGate = checkFeatureFlagGate(
        flags.jfindJsmPaidEditionsMigration
    );

    useEffect(() => {
        const getaaCompleteQueryObj = getLocalStorage(
            AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY
        );
        if (getaaCompleteQueryObj) {
            const parsedStringifyAttributes = JSON.parse(getaaCompleteQueryObj);
            sendConversionEventAlgolia({
                objectID: parsedStringifyAttributes.objectID,
                queryID: parsedStringifyAttributes.queryID,
                position: parsedStringifyAttributes.position
            });
        }
    }, []);

    if (countryCode && blockedLocales.includes(countryCode)) {
        return <ErrorLocale locale={countryCode} />;
    }

    if (userProfile.requestInProgress) {
        return <></>;
    }

    if (!userProfile.isUserLoggedIn && layout !== "standalone") {
        return <LoggedOut />;
    }

    // TO DO: remove is free & is JSW once we are supporting all editions and products
    if (userProfile.isUserLoggedIn && layout !== "standalone") {
        if (isFreeEdition && isJSW && siteActivationGate) {
            return <LoggedIn />;
        }
        if (!isFreeEdition && isJSM && jsmPaidEditionsGate) {
            return <LoggedIn />;
        }
        if (isDevAI) {
            return <LoggedIn />;
        }
    }

    return shouldShowBxpUi({
        productParam,
        bundleOverride,
        editionOverride,
        isNewUser,
        layout,
        migrationSourceUuid,
        developer
    }) ? (
        <ClientOnly>
            <SignupModule
                bundle={bundleOverride as Bundle}
                edition={editionOverride as Edition}
                isNewUser={isNewUser}
            />
        </ClientOnly>
    ) : (
        <ClientOnly>
            <TryIntent
                intentType="form"
                productKey={bundleOverride}
                edition={editionOverride}
            >
                <SignUpLayoutContainer
                    layout={layout}
                    allowSignupWithGoogle={true}
                    allowSignupWithAllSocialOptions={true}
                    bundleOverride={bundleOverride}
                    editionOverride={editionOverride}
                    productData={defaultProductData}
                    enableFreeBreachTest={true}
                    labels={labels}
                    firstImpressionsImageUrls={firstImpressionsImageUrls}
                    devopsImageUrls={devopsImageUrls}
                    featureFlags={featureFlags}
                />
            </TryIntent>
        </ClientOnly>
    );
};

export function shouldShowBxpUi({
    productParam,
    bundleOverride,
    editionOverride,
    isNewUser,
    layout,
    migrationSourceUuid,
    developer
}: {
    productParam?: string;
    bundleOverride?: string;
    editionOverride?: string;
    migrationSourceUuid?: string;
    developer?: string;
    isNewUser?: boolean;
    layout?: LayoutType;
}) {
    const isBxpkitOnly = () => {
        // show Bxpkit to all users with the standalone layout (paid embedded)
        if (layout === "standalone") {
            return true;
        }
        if (migrationSourceUuid) {
            return true;
        }
        if (developer) {
            return true;
        }

        const isMultiProductsParam = productParam?.includes(",");
        if (isMultiProductsParam) {
            return true;
        }
        return false;
    };

    if (isBxpkitOnly()) {
        return false;
    }

    const isJsw = bundleOverride === "jira-software" || !bundleOverride;
    const isConfluence = bundleOverride === "confluence";
    const isJsm = bundleOverride === "jira-service-management";

    const isFreeEdition = editionOverride === "free" || !editionOverride;

    // show Bxpkit if not one of three core products
    const isBxpUiSupportedBundle = isConfluence || isJsw || isJsm;
    if (!isBxpUiSupportedBundle) {
        return false;
    }

    // Jira trials (standard and premium)
    const isJiraTrial = isJsw && !isFreeEdition && isNewUser;
    if (isJiraTrial) {
        return true;
    }

    // Pre-filter conditions for site picker / return to product experiment
    const isEligibleForSitePicker = (isJsw || isConfluence) && isFreeEdition;
    if (isEligibleForSitePicker) {
        return true;
    }

    return false;
}
